/**
 * The condition that reads a COUNTER (D244) — whether an amount the world
 * holds has reached a threshold. Its own module for the reason the record,
 * discovery and body conditions have theirs (the 500-line cap on
 * `conditions.ts`); a member of `conditionSchema`'s union all the same, and
 * its schema arm lives here whole because, unlike the combinators, it holds
 * no condition inside it.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { type CounterId, counterIdSchema } from './identifiers.js';

/**
 * Satisfied when the named counter's value is at least `minimumCount`
 * (D244; the Act II specification's §13.1a).
 *
 * @remarks
 * The one counter read. The others are its spellings, so a reading has one
 * way to be written (D229's rule for `at_least_of`):
 *
 * - "at most k" is `not counter_at_least (k + 1)`;
 * - "exactly k" is `all_of [ counter_at_least k, not counter_at_least (k + 1) ]`;
 * - "empty" is `not counter_at_least (min + 1)`.
 *
 * `minimumCount` is the name `occurrence_tally_at_least` and `at_least_of`
 * already use for the same idea. It may be any integer, because a counter's
 * range may lie below zero; the loader refuses a threshold at or below the
 * counter's `min` (it always holds — spell nothing) and one above its `max`
 * (it never can), and judges whether anything in the pack can carry the
 * counter across it.
 */
export interface CounterAtLeastCondition {
	readonly counterId: CounterId;
	readonly minimumCount: number;
	readonly type: 'counter_at_least';
}

/** The schema arm `conditionSchema`'s union carries for {@link CounterAtLeastCondition}. */
export const counterAtLeastConditionSchema = z.strictObject({
	counterId: counterIdSchema,
	minimumCount: z.int(),
	type: z.literal('counter_at_least'),
});
